import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import * as _ from 'lodash-es';
import { toUsDateString } from '../../shared/_utils/date-utils';

@Injectable({
  providedIn: 'root'
})
export class ScenarioSetupService {

  constructor(private http: HttpClient) { }

  // 🚀 CRITICAL: Accept filterParams and rowData parameters
  saveScenarioSetupForm(
    baseUrl: string,
    formData: any,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    
    // 🚀 CRITICAL: Use correct URL pattern like AngularJS
    const url = `${baseUrl}/saveJsonObject?action_code=3odusa`;

    // 🚀 CRITICAL: Build params exactly like AngularJS
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Add form data exactly like AngularJS
    params = _.extend({ json_data: JSON.stringify(formData) }, params);
    
    // Add jsonSettings exactly like AngularJS
    const jsonSettings = {
      tax_year: filterParams.tax_year || rowData.TAX_YEAR
    };
    params = _.extend({ jsonSettings: JSON.stringify(jsonSettings) }, params);
    
    // Add required configuration parameters
    params.is_issue_key_enabled = 'N';

    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // 🚀 CRITICAL: Update method with proper parameters
  updateScenarioSetupForm(
    baseUrl: string,
    formData: any,
    globalService: any,
    filterParams: any ,
    rowData: any 
  ): Observable<any> {
    
    // 🚀 CRITICAL: Use correct URL pattern like AngularJS
    const url = `${baseUrl}/saveJsonObject?action_code=70fgx1`;

    // 🚀 CRITICAL: Build params exactly like AngularJS
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Add form data exactly like AngularJS (uses jsonObj for update)
    params = _.extend({ jsonObj: JSON.stringify(formData) }, params);
    
    // Add jsonSettings exactly like AngularJS
    const jsonSettings = {
      tax_year: filterParams.tax_year || rowData.TAX_YEAR
    };
    params = _.extend({ jsonSettings: JSON.stringify(jsonSettings) }, params);
    
    // Add required configuration parameters
    params.is_issue_key_enabled = 'N';

    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  // 🚀 CRITICAL: Delete method with proper parameters
  deleteScenarioSetupForm(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    
    // 🚀 CRITICAL: Use correct URL pattern like AngularJS
    const url = `${baseUrl}/saveJsonObject?action_code=c9gpo5`;

    // 🚀 CRITICAL: Build params exactly like AngularJS
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Add delete parameters exactly like AngularJS
    params = _.extend({ 
      tax_year: taxYear,
      scenario_ec: scenario
    }, params);
    
    // Add required configuration parameters
    params.is_issue_key_enabled = 'N';

    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), { ...httpOptions, withCredentials: true });
  }

  loadAllEntities(selectedVal1: string, filterParams: any = {}, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32738,
      action_code: 'm5xlvl',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      group_obj_key: selectedVal1
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  loadGroupObjKey(selectedVal1: string, selectedVal2: string, filterParams: any, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: selectedVal1,
      scenario_desc: selectedVal2
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  loadGroupObjKey898(selectedVal1: string, selectedVal2: string, filterParams: any, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: (filterParams?.tax_year ?? rowData?.TAX_YEAR) - 1,
      scenario_type_code: selectedVal1,
      scenario_desc: selectedVal2
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  loadScenarioTypeCode(filterParams: any, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: null,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  loadScenarioDesc(selectedValue: string, filterParams: any, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: selectedValue,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  loadClientId(filterParams: any = {}, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32761,
      action_code: 'lppcun',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  // 🚀 CRITICAL: Missing API call 32731 - loadScenarioByYear
  loadScenarioByYear(filterParams: any, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32731,
      action_code: 'o2umqh',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  // 🚀 CRITICAL: Missing API call 32023 - loadAllOverrideEntities
  loadAllOverrideEntities(filterParams: any = {}, rowData: any = {}): Observable<any> {
    const baseParams: any = {
      action_id: 32023,
      action_code: 'lvcm1n',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData?.JCD_KEY ?? filterParams?.jcd_key,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  convertDateFormats(jsonObj: any): any {
    const dateFields = [
      'TRADITIONAL_START_DATE',
      'TAX_YEAR_START_DATE',
      'TAX_YEAR_END_DATE',
      'BATCH_START_DATE',
      'BATCH_END_DATE',
      'VIEW_START_DATE',
      'VIEW_END_DATE',
      'IRS_FORM_TY_BEGIN_DATE',
      'IRS_FORM_TY_END_DATE',
    ];

    for (const field of dateFields) {
      if (jsonObj[field]) {
        jsonObj[field] = toUsDateString(jsonObj[field]);
      }
    }

    return jsonObj;
  }

  private getBaseUrl(): string {
    // This should be injected from the component
    return '/gtw';
  }

  private buildFormData(params: any): string {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });
    return urlEncoded.toString();
  }
}
